import { createSignal } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';

import type { ReferenceType } from '@data-access/geospatial/fx-workspace-service';

import type { FxWorkspaceDraftStore } from './create-fx-workspace-draft-store.types';

interface DraftReferenceState {
  readonly references: ReadonlySet<string>;
  readonly types: ReadonlyMap<string, ReferenceType>;
}

type DraftTransition =
  | { readonly kind: 'reset' }
  | { readonly kind: 'replace'; readonly references: ReadonlySet<string> }
  | {
      readonly kind: 'mergeLoaded';
      readonly loadedReferences: ReadonlySet<string>;
    }
  | {
      readonly kind: 'setSelection';
      readonly refTrns: readonly string[];
      readonly selected: boolean;
      readonly type: ReferenceType;
    };

function emptyDraftState(): DraftReferenceState {
  return {
    references: new Set<string>(),
    types: new Map<string, ReferenceType>(),
  };
}

function addLoadedReferences(
  draft: ReadonlySet<string>,
  loadedReferences: ReadonlySet<string>
): Set<string> {
  const next = new Set(draft);
  for (const refTrn of loadedReferences) {
    next.add(refTrn);
  }
  return next;
}

function withSelection(
  state: DraftReferenceState,
  refTrns: readonly string[],
  selected: boolean,
  type: ReferenceType
): DraftReferenceState {
  const references = new Set(state.references);
  const types = new Map(state.types);
  for (const refTrn of refTrns) {
    if (selected) {
      references.add(refTrn);
      types.set(refTrn, type);
    } else {
      references.delete(refTrn);
      types.delete(refTrn);
    }
  }
  return { references, types };
}

function applyDraftTransition(
  previous: DraftReferenceState,
  transition: DraftTransition
): DraftReferenceState {
  switch (transition.kind) {
    case 'reset':
      return emptyDraftState();
    case 'replace':
      return {
        references: new Set(transition.references),
        types: new Map(),
      };
    case 'mergeLoaded':
      return {
        references: addLoadedReferences(
          previous.references,
          transition.loadedReferences
        ),
        types: previous.types,
      };
    case 'setSelection':
      return withSelection(
        previous,
        transition.refTrns,
        transition.selected,
        transition.type
      );
    default:
      throw new UnreachableCaseError(transition);
  }
}

export function createFxWorkspaceDraftStore(): FxWorkspaceDraftStore {
  const [draft, setDraft] =
    createSignal<DraftReferenceState>(emptyDraftState());

  const dispatch = (transition: DraftTransition): void => {
    setDraft((previous) => applyDraftTransition(previous, transition));
  };

  return {
    draftReferences: () => draft().references,
    draftReferenceTypes: () => draft().types,
    reset: (): void => {
      dispatch({ kind: 'reset' });
    },
    replace: (references: ReadonlySet<string>): void => {
      dispatch({ kind: 'replace', references });
    },
    mergeLoaded: (loadedReferences: ReadonlySet<string>): void => {
      dispatch({ kind: 'mergeLoaded', loadedReferences });
    },
    setSelection: (
      refTrns: readonly string[],
      selected: boolean,
      type: ReferenceType
    ): void => {
      dispatch({ kind: 'setSelection', refTrns, selected, type });
    },
  };
}
